import React, { memo, useCallback, useEffect, useRef } from 'react';

import { Box, IconButton, Stack } from '@mui/material';
import { ArrowIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { AiChatMessageRole } from 'constants/aiChat';

import { AiChatTestIds } from '../../AiChat.testIds';
import BotMessage from '../BotMessage';
import ChatStatusMessage from '../ChatStatusMessage';
import PendingQuestionCard from '../PendingQuestionCard';
import ToolConfirmationCard from '../ToolConfirmationCard';
import UserMessage from '../UserMessage';

import {
  contentStyles,
  messagesContainerStyles,
  messagesInnerContainerStyles,
  messagesSpacerStyles,
  scrollToBottomButtonStyles,
} from './AiChatContent.styles';

const CONNECTION_LOST_TITLE = 'Connection Lost.';
const CONNECTION_LOST_DESCRIPTION = 'The AI Assistant is temporarily unavailable';

const AiChatContent = ({
  messages = [],
  isConnected = true,
  isScrolled = false,
  isTyping,
  loanId = null,
  userId,
  displayName,
  onScroll,
}) => {
  const hasMessages = !!messages.length;

  const containerRef = useRef(null);
  const innerContentRef = useRef(null);
  const isInitialScrollDoneRef = useRef(false);
  const isScrolledRef = useRef(isScrolled);

  useEffect(() => {
    isScrolledRef.current = isScrolled;
  }, [isScrolled]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const shouldScrollToBottom = !isInitialScrollDoneRef.current || !isScrolledRef.current;

    if (shouldScrollToBottom) {
      container.scrollTop = container.scrollHeight;
    }

    isInitialScrollDoneRef.current = true;
  }, [messages, isTyping]);

  useEffect(() => {
    const container = containerRef.current;
    const innerContent = innerContentRef.current;

    if (!container || !innerContent) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (!isScrolledRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    });

    resizeObserver.observe(container);
    resizeObserver.observe(innerContent);

    return () => {
      resizeObserver.disconnect();
    };
  }, [hasMessages]);

  const onScrollToBottomClick = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, []);

  return (
    <Stack data-testid={AiChatTestIds.AI_CHAT_CONTENT} sx={contentStyles}>
      <Stack
        data-testid={AiChatTestIds.AI_CHAT_CONTENT_MESSAGE_LIST}
        ref={containerRef}
        sx={messagesContainerStyles}
        onScroll={onScroll}
      >
        {hasMessages && (
          <Box ref={innerContentRef} sx={messagesInnerContainerStyles}>
            <Box sx={messagesSpacerStyles} />
            {messages.map(message => {
              if (message.role === AiChatMessageRole.USER) {
                // DEV-4392 / DEV-4430: on reload/reconnect the parked
                // ask_user_question and parked tool confirmations are both owned
                // by the user message (no assistant message persists at suspend
                // time — see ChatMessageProcessor), so the recovered cards hang
                // off it here. Both cards self-gate by messageId (render null when
                // nothing is pending), mirroring their live-path BotMessage hosts.
                return (
                  <React.Fragment key={message.id}>
                    <UserMessage displayName={displayName} message={message} userId={userId} />
                    <PendingQuestionCard messageId={message.id} />
                    <ToolConfirmationCard messageId={message.id} />
                  </React.Fragment>
                );
              }

              if (message.role === AiChatMessageRole.TOOL) {
                return null;
              }

              return <BotMessage key={message.id} loanId={loanId} message={message} />;
            })}
            {!isConnected && (
              <ChatStatusMessage
                description={CONNECTION_LOST_DESCRIPTION}
                title={CONNECTION_LOST_TITLE}
              />
            )}
          </Box>
        )}
      </Stack>

      {isScrolled && (
        <IconButton
          aria-label="Scroll to bottom"
          data-testid={AiChatTestIds.AI_CHAT_CONTENT_SCROLL_TO_BOTTOM}
          sx={scrollToBottomButtonStyles}
          onClick={onScrollToBottomClick}
        >
          <ArrowIcon direction="down" size={16} />
        </IconButton>
      )}
    </Stack>
  );
};

AiChatContent.propTypes = {
  isTyping: PropTypes.bool.isRequired,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      role: PropTypes.oneOf(Object.values(AiChatMessageRole)).isRequired,
      content: PropTypes.string,
      timestamp: PropTypes.string,
      status: PropTypes.string,
    }),
  ),
  isConnected: PropTypes.bool,
  isScrolled: PropTypes.bool,
  loanId: PropTypes.string,
  userId: PropTypes.string,
  displayName: PropTypes.string,
  onScroll: PropTypes.func,
};

export default memo(AiChatContent);
